<template>
  <div class="lg:w-full chart-container" :style="{ 'min-height': minHeigth, 'height': heightContainer }">
    <canvas :id="id" :aria-label="infoResumenTexto" tabindex="0"></canvas>
  </div>
</template>

<script>
import moment from 'moment'
import { i18n } from '@/i18n'

import colorsMixin from '@/mixins/colorsMixin'

import Chart from 'chart.js'
import { capitalize, isNil, uniq } from 'lodash'

function pickValue (...values) {
  const lastValue = values.pop()
  for (const value of values) {
    if (value === '' || isNil(value)) {
      continue
    }
    return value
  }
  return lastValue
}


export default {
  name: 'Chart',
  mixins: [colorsMixin],
  props: {
    type: {
      type: String,
      default: 'bar'
    },
    id: {
      type: String,
      default: 'chart'
    },
    label: {
      type: String,
      default: 'chart'
    },
    charData: {
      type: [Array, Object]
    },
    expand: {
      type: Boolean,
      default: false,
    },
    stacked: {
      type: Boolean,
      default: false,
    },
    showLegend: {
      type: Boolean,
      default: true,
    },
    backgroundColor: {
      type: String,
      default: null,
    },
    borderColor: {
      type: String,
      default: null,
    },
    darkBackgroundColor: {
      type: String,
      default: null,
    },
    darkBorderColor: {
      type: String,
      default: null,
    },
    parseFormat: {
      type: [String, Array],
      default: null,
    },
    tooltipTitle: {
      type: Function,
      default: null,
    },
    tooltipTitleValueFormat: {
      type: String,
      default: null,
    },
    tooltipTitlePrefix: {
      type: String,
      default: null,
    },
    tooltipTitleSuffix: {
      type: String,
      default: null,
    },
    tooltipLabel: {
      type: Function,
      default: null,
    },
    tooltipValueFormat: {
      type: String,
      default: null,
    },
    tooltipPrefix: {
      type: String,
      default: null,
    },
    tooltipSuffix: {
      type: String,
      default: null,
    },
    tooltipXValueFormat: {
      type: String,
      default: null,
    },
    tooltipXPrefix: {
      type: String,
      default: null,
    },
    tooltipXSuffix: {
      type: String,
      default: null,
    },
    tooltipYValueFormat: {
      type: String,
      default: null,
    },
    tooltipYPrefix: {
      type: String,
      default: null,
    },
    tooltipYSuffix: {
      type: String,
      default: null,
    },
    tooltipRValueFormat: {
      type: String,
      default: null,
    },
    tooltipRPrefix: {
      type: String,
      default: null,
    },
    tooltipRSuffix: {
      type: String,
      default: null,
    },
    xAxisLabel: {
      type: Function,
      default: null,
    },
    xAxisValueFormat: {
      type: String,
      default: null,
    },
    xAxisPrefix: {
      type: String,
      default: null,
    },
    xAxisSuffix: {
      type: String,
      default: null,
    },
    yAxisLabel: {
      type: Function,
      default: null,
    },
    yAxisValueFormat: {
      type: String,
      default: null,
    },
    yAxisPrefix: {
      type: String,
      default: null,
    },
    yAxisSuffix: {
      type: String,
      default: null,
    },
  },
  data () {
    return {
      ChartData: null,
      myChart: null,
      defaultColors: {
        backgroundColor: this.getColor('--primary-300'),
        borderColor: this.getColor('--primary-600'),
        darkBackgroundColor: this.getColor('--primary-DEFAULT'),
        darkBorderColor: this.getColor('--primary-600'),
      }
    }
  },
  computed: {
    minHeigth () {
      let heigth = 150
      if (Array.isArray(this.charData)) {
        const actualHeightAux = (this.charData.length + 1) * 28
        if (actualHeightAux > heigth) {
          heigth = actualHeightAux
        }
      }
      return heigth + 'px'
    },
    heightContainer () {
      return this.expand ? '50vh' : '30vh'
    },
    darkMode: function () {
      return this.$store.getters['app/darkMode']
    },
    infoResumenTexto () {
      var msg = ''
      if (Array.isArray(this.charData)) {
        msg += this.label + '. '
        this.charData.forEach(x => {
          msg += '' + x.label + ': ' + x.value + '; '
        })
      }
      return msg
    },
    infoResumenTextoFormat () {
      var msg = ''
      if (Array.isArray(this.charData)) {
        msg += `<b>${this.label}</b><br/>`
        this.charData.forEach(x => {
          msg += '' + x.label + ': ' + x.value + '<br/>'
        })
      }
      return msg
    },
    titleTooltipOptions () {
      return {
        format: this.tooltipTitleValueFormat ?? this.tooltipXValueFormat ?? this.xAxisValueFormat,
        prefix: this.tooltipTitlePrefix ?? this.tooltipXPrefix ?? this.xAxisPrefix ?? '',
        suffix: this.tooltipTitleSuffix ?? this.tooltipXSuffix ?? this.xAxisSuffix ?? ''
      }
    },
    labelTooltipOptions () {
      return {
        format: this.tooltipValueFormat ?? this.tooltipYValueFormat ?? this.yAxisValueFormat,
        prefix: this.tooltipPrefix ?? this.tooltipYPrefix ?? this.yAxisPrefix ?? '',
        suffix: this.tooltipSuffix ?? this.tooltipYSuffix ?? this.yAxisSuffix ?? ''
      }
    },
    xAxisTooltipOptions () {
      return {
        format: this.tooltipXValueFormat ?? this.tooltipTitleValueFormat ?? this.xAxisValueFormat,
        prefix: this.tooltipXPrefix ?? this.tooltipTitlePrefix ?? this.xAxisPrefix ?? '',
        suffix: this.tooltipXSuffix ?? this.tooltipTitleSuffix ?? this.xAxisSuffix ?? ''
      }
    },
    yAxisTooltipOptions () {
      return {
        format: this.tooltipYValueFormat ?? this.tooltipValueFormat ?? this.yAxisValueFormat,
        prefix: this.tooltipYPrefix ?? this.tooltipPrefix ?? this.yAxisPrefix ?? '',
        suffix: this.tooltipYSuffix ?? this.tooltipSuffix ?? this.yAxisSuffix ?? ''
      }
    },
    radiusTooltipOptions () {
      return {
        format: this.tooltipRValueFormat ?? this.tooltipValueFormat,
        prefix: this.tooltipRPrefix ?? this.tooltipPrefix ?? '',
        suffix: this.tooltipRSuffix ?? this.tooltipSuffix ?? ''
      }
    },
    xAxisTickOptions () {
      return {
        format: this.xAxisValueFormat,
        prefix: this.xAxisPrefix ?? '',
        suffix: this.xAxisSuffix ?? ''
      }
    },
    yAxisTickOptions () {
      return {
        format: this.yAxisValueFormat,
        prefix: this.yAxisPrefix ?? '',
        suffix: this.yAxisSuffix ?? ''
      }
    },
  },
  watch: {
    charData: 'dataChangeHandler',
    darkMode: 'changeDarkmode',
  },
  methods: {
    initDatasetColors (dataset) {
      dataset.lightBackgroundColor = Object.hasOwnProperty.call(dataset, 'lightBackgroundColor') ? dataset.lightBackgroundColor : dataset.backgroundColor
      dataset.lightBorderColor = Object.hasOwnProperty.call(dataset, 'lightBorderColor') ? dataset.lightBorderColor : dataset.borderColor
      dataset.backgroundColor = this.getBackgroundColor(dataset)
      dataset.borderColor = this.getBorderColor(dataset)
      return dataset
    },
    initDatasetsColors (datasets) {
      if (datasets) {
        return datasets.map(this.initDatasetColors)
      }
      return datasets
    },
    getBackgroundColor (dataset) {
      if (this.darkMode) {
        return dataset?.darkBackgroundColor || dataset?.lightBackgroundColor || this.darkBackgroundColor || this.backgroundColor || this.defaultColors.darkBackgroundColor || this.defaultColors.backgroundColor
      } else {
        return dataset?.lightBackgroundColor || this.backgroundColor || this.defaultColors.backgroundColor
      }
    },
    getBorderColor (dataset) {
      if (this.darkMode) {
        return dataset?.darkBorderColor || dataset?.lightBorderColor || this.darkBorderColor || this.borderColor || this.defaultColors.darkBorderColor || this.defaultColors.borderColor
      } else {
        return dataset?.lightBorderColor || this.borderColor || this.defaultColors.borderColor
      }
    },
    changeDarkmode: function () {
      Chart.defaults.global.defaultFontColor = this.darkMode ? this.getColor('--secondary-100') : this.getColor('--secondary-DEFAULT')
      if ((this.myChart.config.type === 'bar' || this.myChart.config.type === 'horizontalBar') && !this.myChart.config.options.scales?.xAxes[0]?.stacked) {
        this.myChart.config.options.scales.xAxes.forEach(x => {
          x.gridLines.color = this.darkMode ? this.getColor('--darkblue-400') : this.getColor('--primary-300') 
        })
        this.myChart.config.options.scales.yAxes.forEach(x => {
          x.gridLines.color = this.darkMode ? this.getColor('--darkblue-400') : this.getColor('--primary-300') 
          x.gridLines.zeroLineColor = this.darkMode ? this.getColor('--darkblue-400') : this.getColor('--primary-300') 
        })
      } else if (this.myChart.config.type === 'line' || this.myChart.config.options.scales?.xAxes[0]?.stacked) {
        this.myChart.config.options.scales.yAxes.forEach(x => {
          x.gridLines.color = this.darkMode ? this.getColor('--darkblue-400') : this.getColor('--primary-300') 
          x.gridLines.zeroLineColor = this.darkMode ? this.getColor('--darkblue-400') : this.getColor('--primary-300') 
        })
        this.myChart.config.options.scales.xAxes.forEach(x => {
          x.gridLines.color = this.darkMode ? this.getColor('--darkblue-400') : this.getColor('--primary-300') 
          x.gridLines.zeroLineColor = this.darkMode ? this.getColor('--darkblue-400') : this.getColor('--primary-300') 
        })
      }
      this.myChart.config.data.datasets.forEach(x => {
        x.backgroundColor = this.getBackgroundColor(x)
        x.borderColor = this.getBorderColor(x)
      })
      this.myChart.config.options.legend.labels.fontColor = this.darkMode ? this.getColor('--secondary-100') : this.getColor('--secondary-DEFAULT') 
      this.updateFixChart()
    },
    updateFixChart () {
      this.myChart.update()
      setTimeout(() => {
        this.myChart.update()
        this.$emit('chartUpdated', this.infoResumenTextoFormat)
      }, 100)
    },
    removeData () {
      this.myChart.data.labels = []
      
      for (let index = 0; index < this.myChart.data.datasets.length; index++) {
        this.myChart.data.datasets[index].data = []
      }
      // this.myChart.data.labels.pop()
      // this.myChart.data.datasets.forEach(x => x.data.pop())
      this.updateFixChart()
    },
    addData (label, data) {
      this.myChart.data.labels.push(label)
      this.myChart.data.datasets.forEach(x => x.data.push(data))
      this.updateFixChart()
    },
    normalizeTooltipParams (tooltipItem, data) {
      const index = tooltipItem.index
      const datasetIndex = tooltipItem.datasetIndex
      const dataset = (data.datasets && data.datasets[datasetIndex]) || {}
      const datasetType = dataset.type || this.type
      const itemData = dataset.data?.[index]
      return {
        label: pickValue(data.labels?.[index], ''),
        value: pickValue(tooltipItem.value, itemData, 0),
        radius: pickValue(itemData?.r, undefined),
        datasetLabel: pickValue(dataset.label, ''),
        datasetIndex,
        datasetType,
        index,
      }
    },
    formatLabelValue (value, { format, prefix, suffix } = {}) {
      if (typeof value === 'string' && this.parseFormat?.length && format?.length && !i18n.ne(format)) {
        const valueMoment = moment(value, this.parseFormat, true)
        if (valueMoment.isValid()) {
          value = valueMoment.valueOf()
        }
      }
      const numericValue = Number(value)
      const isNumericValue = Number.isFinite(numericValue)
      let formattedValue = value
      
      if (isNumericValue) {
        if (i18n.ne(format)) {
          formattedValue = i18n.n(numericValue, format)
        } else if (i18n.de(format)) {
          formattedValue = capitalize(i18n.d(numericValue, format))
        } else if (format) {
          formattedValue = moment(numericValue).locale(i18n.locale).format(format)
        } else {
          formattedValue = i18n.n(numericValue, 'decimal')
        }
      }
      return `${prefix} ${formattedValue} ${suffix}`.trim()
    },
    getAxisTickLabel (value, xAxis) {
      const [label, options] = xAxis
        ? [this.xAxisLabel, this.xAxisTickOptions]
        : [this.yAxisLabel, this.yAxisTickOptions]
      if (typeof label === 'function') {
        return label(value, xAxis)
      }
      return this.formatLabelValue(value, options)
    },
    getRadarAxisMinValue (data) {
      const scaleMin = this.myChart?.scale?.min
      if (Number.isFinite(scaleMin)) {
        return scaleMin
      }

      const numericValues = (data?.datasets || [])
        .flatMap(dataset => dataset?.data || [])
        .map(value => Number(value))
        .filter(Number.isFinite)

      return numericValues.length > 0 ? Math.min(...numericValues) : null
    },
    getTooltipLabel (tooltipItem, data) {
      const params = this.normalizeTooltipParams(tooltipItem, data)

      if (typeof this.tooltipLabel === 'function') {
        return this.tooltipLabel(params)
      }
      
      const useLabelByChartType = ['doughnut', 'pie', 'polarArea'].includes(params.datasetType)
  
      const xValue = this.formatLabelValue(params.label, this.xAxisTooltipOptions)
      const yValue = this.formatLabelValue(params.value, this.yAxisTooltipOptions)
      const tooltipTitle = useLabelByChartType ? xValue : params.datasetLabel
  
        
      // Incluir label del eje solo cuando el mínimo aparece en índices distintos (labels distintos).
      let prefix = ''
      if (params.datasetType === 'radar') {
        const radarAxisMinValue = this.getRadarAxisMinValue(data)
        const numericPointValue = Number(params.value)
        const isSameMinValue = Number.isFinite(radarAxisMinValue) && Number.isFinite(numericPointValue) && numericPointValue === radarAxisMinValue
        const isRadarWithMultipleIndices = isSameMinValue && data.datasets.some((dataset, i) => (dataset.data || []).some((x, j) => {
          const numericValue = Number(x)
          return Number.isFinite(numericValue) && numericValue === radarAxisMinValue && j !== params.index
        }))
        prefix = isRadarWithMultipleIndices ? `${xValue} - ` : ''
      }
        
      if (params.datasetType === 'scatter') {
        return `${tooltipTitle}: (${xValue}, ${yValue})`
      }
      if (params.datasetType === 'bubble') {
        const rValue = this.formatLabelValue(params.radius, this.radiusTooltipOptions)
        return `${tooltipTitle}: (${xValue}, ${yValue}, ${rValue})`
      }
      return `${prefix}${tooltipTitle}: ${yValue}`
    },
    getTooltipTitle (tooltipItems, data) {
      const params = tooltipItems.map(tooltipItem => this.normalizeTooltipParams(tooltipItem, data))
      if (params.every(param => !['bar', 'line', 'radar', 'horizontalBar'].includes(param.datasetType))) {
        return null
      }
      if (typeof this.tooltipTitle === 'function') {
        return this.tooltipTitle(params)
      }
      
      return uniq(params.map(param => param.label)).map(x => this.formatLabelValue(x, this.titleTooltipOptions)).join(' - ')
    },
    dataChangeHandler: function () {
      const isMultiDataset = this.charData && !Array.isArray(this.charData) && this.charData.labels && this.charData.datasets
      if (this.myChart !== null && this.myChart !== undefined) {
        if (isMultiDataset) {
          this.myChart.data.labels = this.charData.labels
          this.myChart.data.datasets = this.initDatasetsColors(this.charData.datasets)
          this.updateFixChart()
        } else {
          this.removeData()
          this.charData.forEach(element => {
            this.addData(element.label, element.value)
          })
          this.myChart.data.datasets = this.initDatasetsColors(this.myChart.data.datasets)
        }
      } else {
        const self = this
        Chart.defaults.global.defaultFontColor = this.darkMode ? this.getColor('--secondary-100') : this.getColor('--secondary-DEFAULT') 
        const ctx = document.getElementById(this.id)
        if (isMultiDataset) {
          this.ChartData = {
            type: this.type,
            responsive: true,
            data: {
              labels: this.charData.labels,
              datasets: this.initDatasetsColors(this.charData.datasets)
            },
            options: {
              legend: {
                display: !!this.showLegend,
                labels: {
                  // fontColor: this.darkMode ? this.getColor('--secondary-100') : this.getColor('--secondary-DEFAULT'),
                  fontSize: 14,
                }
              },
              maintainAspectRatio: false,
              ...(!['doughnut', 'pie', 'polarArea', 'radar'].includes(this.type) ? {
                scales: {
                  xAxes: [{
                    stacked: this.stacked,
                    gridLines: {
                      color: this.darkMode ? this.getColor('--darkblue-400') : this.getColor('--primary-300'),
                    },
                    ticks: {
                      fontSize: 14,
                      callback: value => self.getAxisTickLabel(value, true)
                    }
                  }],
                  yAxes: [{
                    stacked: this.stacked,
                    gridLines: {
                      color: this.darkMode ? this.getColor('--darkblue-400') : this.getColor('--primary-300'),
                      zeroLineColor: this.darkMode ? this.getColor('--darkblue-400') : this.getColor('--primary-300')
                    },
                    ticks: {
                      beginAtZero: true,
                      fontSize: 14,
                      callback: value => self.getAxisTickLabel(value, false)
                    }
                  }]
                }
              } : {}),
              tooltips: {
                callbacks: {
                  title: (tooltipItem, data) => self.getTooltipTitle(tooltipItem, data),
                  label: (tooltipItem, data) => self.getTooltipLabel(tooltipItem, data)
                }
              },
              onHover: (event, chartElement) => {
                const target = event.native ? event.native.target : event.target
                target.style.cursor = chartElement[0] ? 'pointer' : 'default'
              }
            }
          }
          const myChart = new Chart(ctx, this.ChartData)
          this.myChart = myChart
          ctx.onclick = function (evt) {
            const activePoint = myChart.getElementAtEvent(evt)[0]
            if (activePoint && activePoint._chart) {
              const data = activePoint._chart.data
              const datasetIndex = activePoint._datasetIndex
              const label = data.labels[activePoint._index]
              const value = data.datasets[datasetIndex].data[activePoint._index]
              self.$emit('callback', label, value)
            }
          }
          this.updateFixChart()
          return
        }
        const labels = []
        const values = []
        this.charData.forEach(element => {
          labels.push(element.label)
          values.push(element.value)
        })
        this.ChartData = null
        this.ChartData = {
          type: this.type, // 'horizontalBar',
          data: {
            labels: labels,
            datasets: this.initDatasetsColors([
              {
                label: this.label,
                data: values,
                borderWidth: 2,
                maxBarThickness: 40,
                fontSize: 40,
              },
            ])
          },
          options: {
            legend: {
              display: !!this.showLegend,
              labels: {
                fontColor: this.darkMode ? this.getColor('--secondary-100') : this.getColor('--secondary-DEFAULT'),
                fontSize: 14,
              }
            },
            maintainAspectRatio: false,
            scales: {
              xAxes: [{
                stacked: true,
                gridLines: {
                  color: this.darkMode ? this.getColor('--darkblue-400') : this.getColor('--primary-300'),
                },
                ticks: {
                  precision: 0,
                  fontSize: 14,
                  callback: value => self.getAxisTickLabel(value, true)
                }
              }],
              yAxes: [{
                // TMP Stacked 
                // stacked: true,
                gridLines: {
                  color: this.darkMode ? this.getColor('--darkblue-400') : this.getColor('--primary-300'),
                  zeroLineColor: this.darkMode ? this.getColor('--darkblue-400') : this.getColor('--primary-300') 
                },
                ticks: {
                  beginAtZero: true,
                  fontSize: 14,
                  callback: value => self.getAxisTickLabel(value, false)
                }
              }]
            },
            tooltips: {
              callbacks: {
                title: (tooltipItem, data) => self.getTooltipTitle(tooltipItem, data),
                label: (tooltipItem, data) => self.getTooltipLabel(tooltipItem, data)
              },
            },
            onHover: (event, chartElement) => {
              const target = event.native ? event.native.target : event.target
              target.style.cursor = chartElement[0] ? 'pointer' : 'default'
            }
          }
        }
        // eslint-disable-next-line no-new
        const myChart = new Chart(ctx, this.ChartData)
        this.myChart = myChart
        ctx.onclick = function (evt) {
          const activePoint = myChart.getElementAtEvent(evt)[0]
          if (activePoint && activePoint._chart) {
            const data = activePoint._chart.data
            const datasetIndex = activePoint._datasetIndex
            const label = data.labels[activePoint._index]
            const value = data.datasets[datasetIndex].data[activePoint._index]
            self.$emit('callback', label, value)
          }
        }
        this.updateFixChart()
      }
    },
    toggleDatasetVisibility (index) {
      let meta = {}
      if (this.type === 'line' || this.type === 'bar' || this.type === 'horizontalBar') {
        meta = this.myChart.getDatasetMeta(index)
        meta.hidden = meta.hidden === null ? !this.myChart.data.datasets[index].hidden : null
      } else if (this.type === 'doughnut') {
        meta = this.myChart.getDatasetMeta(0)
        meta.data[index].hidden = meta.hidden === null ? !meta.data[index].hidden : null
      }
      this.myChart.update()
    },
    trimCanvas () {
      const canvas = this.$el.querySelector('canvas')
      if (!canvas) return null
      const ctx = canvas.getContext('2d')
      const { width, height } = canvas
      const imageData = ctx.getImageData(0, 0, width, height)
      const { data } = imageData
      let top = height
      let left = width
      let right = 0
      let bottom = 0

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          if (data[(y * width + x) * 4 + 3] > 0) {
            if (y < top) top = y
            if (y > bottom) bottom = y
            if (x < left) left = x
            if (x > right) right = x
          }
        }
      }

      if (right < left || bottom < top) return canvas

      const w = right - left + 1
      const h = bottom - top + 1
      const trimmed = document.createElement('canvas')
      trimmed.width = w
      trimmed.height = h
      trimmed.getContext('2d').drawImage(canvas, left, top, w, h, 0, 0, w, h)
      return trimmed
    }
  }
}
</script>

<style scoped>

.chart-container {
  position: relative;
  /* height: 30vh; */
  /* height: 100%; */
}
</style>
